import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import countryCodesData from 'i18n-iso-countries/codes.json';
import dayjs from 'dayjs';
import { setOptions } from 'highcharts';
import localesIt from '../assets/locales/it.json';
import localesEs from '../assets/locales/es.json';
import localesDe from '../assets/locales/de.json';
import localesFr from '../assets/locales/fr.json';
import localesNl from '../assets/locales/nl.json';
import localesNlNlOverrides from '../assets/locales/nl-NL.overrides.json';
import localesEn from '../assets/locales/en.json';
import localesUs from '../assets/locales/us.json';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import { showUsaFeature } from './app';

export enum LanguageCode {
  American_English = 'US',
  English = 'EN',
  Dutch = 'NL',
  French = 'FR',
  German = 'DE',
  Spanish = 'ES',
  Italian = 'IT',
}

export const DEFAULT_LANGUAGE = showUsaFeature ? LanguageCode.American_English : LanguageCode.English;
export const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

void i18next.use(initReactI18next).init({
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
});

i18next.addResourceBundle(LanguageCode.American_English, 'translation', localesUs);
i18next.addResourceBundle(LanguageCode.English, 'translation', localesEn);
i18next.addResourceBundle(LanguageCode.Dutch, 'translation', localesNl);
i18next.addResourceBundle(LanguageCode.French, 'translation', localesFr);
i18next.addResourceBundle(LanguageCode.German, 'translation', localesDe);
i18next.addResourceBundle(LanguageCode.Spanish, 'translation', localesEs);
i18next.addResourceBundle(LanguageCode.Italian, 'translation', localesIt);

dayjs.Ls.en.weekStart = 1;

export const PickEnForAmericanEnglish = () => {
  if (i18next.language === LanguageCode.American_English) return LanguageCode.English;

  if (i18next.language) {
    return i18next.language;
  }
  return LanguageCode.English;
};

// ISO 3166-1 alpha-2 codes, sourced from i18n-iso-countries's static code table (this package
// is used only for that stable list - the localized names themselves come from Intl.DisplayNames).
const COUNTRY_ALPHA2_CODES = (countryCodesData as [string, string, string, string][]).map(([alpha2]) => alpha2);

const LANGUAGE_TO_LOCALE: Record<string, string> = {
  [LanguageCode.American_English]: 'en',
  [LanguageCode.English]: 'en',
  [LanguageCode.Dutch]: 'nl',
  [LanguageCode.French]: 'fr',
  [LanguageCode.German]: 'de',
  [LanguageCode.Spanish]: 'es',
  [LanguageCode.Italian]: 'it',
};

const getCountryDisplayNames = (lang: string) =>
  new Intl.DisplayNames([LANGUAGE_TO_LOCALE[lang] ?? 'en'], { type: 'region' });

export const getUserCountry = (country = '') => {
  if (!country) return '';
  return getCountryDisplayNames(i18next.language).of(country) || '';
};

/**
 * Atrial fibrillation terminology differs between Dutch dialects: 'voorkamer' (BE, the
 * nl.json default) vs 'boezem' (NL). The stored `language` value stays 'NL' for everyone
 * , so the dialect is resolved from the user's `country` instead
 * and layered on top of the base bundle at runtime.
 */
export const setDutchTerminology = (country?: string) => {
  i18next.addResourceBundle(LanguageCode.Dutch, 'translation', localesNl, true, true);
  if (country === 'NL') {
    i18next.addResourceBundle(LanguageCode.Dutch, 'translation', localesNlNlOverrides, true, true);
  }
};

/**
 * @param priceInCents - The price you want to format in cents (1000)
 * @param locale - The locale you want to format the price for (default is nl-BE)
 * @returns The formatted price
 */
export function formatPrice(priceInCents: number, locale = showUsaFeature ? 'en-US' : 'nl-BE') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: showUsaFeature ? 'USD' : 'EUR',
    compactDisplay: 'short',
  }).format(priceInCents / 100);
}

export function setHighChartsOptions(locale: string) {
  const formatDate = (date?: number | Date, options?: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, options).format(date);

  const formattedNumber = new Intl.NumberFormat(locale).formatToParts(99999.99);

  const decimalPoint = formattedNumber.find((part) => part.type === 'decimal');
  const thousandsSep = formattedNumber.find((part) => part.type === 'group');

  // to get the days of the week and months we must use real dates and parse them
  // the dates used don't matter
  const daysOfWeek = new Array(7)
    .fill(0)
    .map((_, i) => {
      const date = new Date(2021, 1, i + 1);
      return {
        day: date.getDay(),
        weekday: formatDate(date, {
          weekday: 'long',
        }),
        shortWeekday: formatDate(date, {
          weekday: 'short',
        }),
      };
    })
    .sort((a, b) => a.day - b.day);

  const months = new Array(12)
    .fill(0)
    .map((_, i) => {
      const date = new Date(2021, i + 1);
      return {
        month: date.getMonth(),
        shortMonth: formatDate(date, {
          month: 'short',
        }),
        longMonth: formatDate(date, {
          month: 'long',
        }),
      };
    })
    .sort((a, b) => a.month - b.month);

  setOptions({
    lang: {
      locale,
      decimalPoint: decimalPoint?.value,
      thousandsSep: thousandsSep?.value,
      weekdays: daysOfWeek.map((d) => d.weekday),
      shortWeekdays: daysOfWeek.map((d) => d.shortWeekday),
      months: months.map((d) => d.longMonth),
      shortMonths: months.map((d) => d.shortMonth),
    },
    time: {
      timezone: BROWSER_TIMEZONE,
    },
  });
}

export const completeCountryList = (lang: string = i18next.language) => {
  const displayNames = getCountryDisplayNames(lang);
  return COUNTRY_ALPHA2_CODES.map((code) => ({
    label: displayNames.of(code) || '',
    value: code,
  })).filter((entry) => entry.label !== '');
};
